/**
 * NCR events for the PO Line Detail tab's Audit History.
 *
 * Customer review 11 Sep (item 30a): PRO0024669 line 1 carries NCR 3252
 * (reject qty 1), yet its Audit History listed only the GRN. The backend feed
 * synthesises GRN and delivery-appeal events but never NCRs. These entries are
 * built from the very list the NCR tab shows — the NCR endpoint, which already
 * applies the customer / supplier visibility rules (drafts, internal NCRs,
 * liability) — so the audit can never mention an NCR the viewer cannot open.
 */
import type { NonConformanceReport } from '../../lib/api';
import type { ChangelogEntry } from '../common/Changelog';

/** Offset keeping synthetic NCR ids clear of real and GRN/appeal synthetic ids. */
const NCR_EVENT_ID_OFFSET = 3_000_000_000;

/**
 * One Audit History entry per raised NCR (drafts are not raised yet).
 *
 * @param ncrs - The line's NCRs as returned by `qualityAPI.getNCRs({ po_line })`.
 * @returns Entries shaped for {@link ChangelogEntry}, each linking to its NCR.
 */
export function ncrAuditEntries(ncrs: NonConformanceReport[]): ChangelogEntry[] {
  return ncrs
    .filter((ncr) => ncr.status !== 'draft')
    .map((ncr) => ({
      id: -(NCR_EVENT_ID_OFFSET + ncr.id),
      change_date: ncr.ncr_date || ncr.raised_date,
      changed_by: ncr.raised_by_name || 'Not recorded',
      field_changed: 'ncr_raised',
      old_value: null,
      new_value: `NCR ${ncr.ncr_number} · Reject qty ${ncr.reject_qty ?? 0}`,
      reason: [ncr.severity_display, ncr.status_display].filter(Boolean).join(' · '),
      event_type: 'ncr',
      link: { href: `/quality/ncr/${ncr.id}/edit`, label: `Open NCR ${ncr.ncr_number}` },
    }));
}
